/**
 * CierreAtencionDialog — Diálogo de cierre con verificación de completitud.
 *
 * Requirements: M4.9.1, M4.9.3, M4.9.4, M4.9.5, M4.15.6
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useVerificarCompletitud } from "@/hooks/use-muestras";
import type { CerrarAtencionData, TipoIncidencia } from "@/hooks/use-muestras";

interface CierreAtencionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  atencionId: number;
  onConfirm: (data: CerrarAtencionData) => void;
  isLoading?: boolean;
}

const TIPOS_INCIDENCIA: { value: TipoIncidencia; label: string }[] = [
  { value: "ABANDONO", label: "Abandono del paciente" },
  { value: "NO_PRESENTACION", label: "No presentación" },
  { value: "SUSPENSION", label: "Suspensión médica" },
];

export function CierreAtencionDialog({
  open,
  onOpenChange,
  atencionId,
  onConfirm,
  isLoading,
}: CierreAtencionDialogProps) {
  const { t } = useTranslation();
  const [tipoCierre, setTipoCierre] = useState<"TOTAL" | "PARCIAL" | "CANCELAR">("TOTAL");
  const [justificacion, setJustificacion] = useState("");
  const [tipoIncidencia, setTipoIncidencia] = useState<TipoIncidencia | "">("");
  const [descripcionIncidencia, setDescripcionIncidencia] = useState("");

  const { data: completitud } = useVerificarCompletitud(atencionId, { enabled: open });

  const puedeTotal = completitud?.completa ?? false;
  const requiereJustificacion = tipoCierre === "PARCIAL";
  const requiereIncidencia = tipoCierre === "CANCELAR";

  const puedeConfirmar =
    (tipoCierre === "TOTAL" && puedeTotal) ||
    (tipoCierre === "PARCIAL" && justificacion.trim().length > 0) ||
    (tipoCierre === "CANCELAR" && tipoIncidencia !== "");

  const handleConfirm = () => {
    if (!puedeConfirmar) return;
    onConfirm({
      tipo_cierre: tipoCierre,
      justificacion: justificacion.trim() || undefined,
      incidencia: tipoIncidencia
        ? { tipo: tipoIncidencia, descripcion: descripcionIncidencia.trim() }
        : null,
    });
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setTipoCierre("TOTAL");
      setJustificacion("");
      setTipoIncidencia("");
      setDescripcionIncidencia("");
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("muestras.cierre.titulo")}</DialogTitle>
          <DialogDescription>{t("muestras.cierre.descripcion")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Estado de completitud */}
          {completitud && (
            <div
              className={`flex items-center gap-2 rounded-md p-3 text-sm ${
                completitud.completa
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-yellow-50 text-yellow-800 border border-yellow-200"
              }`}
            >
              {completitud.completa ? (
                <CheckCircle className="h-4 w-4 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 shrink-0" />
              )}
              <span>
                {completitud.completa
                  ? t("muestras.cierre.todas_definidas")
                  : t("muestras.cierre.muestras_pendientes", {
                      count: completitud.muestras_pendientes,
                    })}
              </span>
            </div>
          )}

          {/* Tipo de cierre */}
          <div className="space-y-2">
            <Label>{t("muestras.cierre.tipo_cierre")}</Label>
            <div className="flex gap-2">
              {(["TOTAL", "PARCIAL", "CANCELAR"] as const).map((tipo) => (
                <Button
                  key={tipo}
                  variant={tipoCierre === tipo ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTipoCierre(tipo)}
                  disabled={tipo === "TOTAL" && !puedeTotal}
                >
                  {t(`muestras.cierre.tipo_${tipo.toLowerCase()}`)}
                </Button>
              ))}
            </div>
          </div>

          {/* Justificación para cierre parcial */}
          {requiereJustificacion && (
            <div className="space-y-1">
              <Label htmlFor="justificacion">
                {t("muestras.cierre.justificacion")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="justificacion"
                rows={2}
                placeholder={t("muestras.cierre.justificacion_placeholder")}
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
              />
            </div>
          )}

          {/* Incidencia para cancelar */}
          {requiereIncidencia && (
            <div className="space-y-2">
              <div className="space-y-1">
                <Label>
                  {t("muestras.incidencia.tipo")}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Select
                  value={tipoIncidencia}
                  onValueChange={(val) => setTipoIncidencia(val as TipoIncidencia)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("muestras.incidencia.selecciona")} />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_INCIDENCIA.map((ti) => (
                      <SelectItem key={ti.value} value={ti.value}>
                        {ti.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="desc_incidencia">{t("muestras.incidencia.descripcion")}</Label>
                <Textarea
                  id="desc_incidencia"
                  rows={2}
                  placeholder={t("muestras.incidencia.descripcion_placeholder")}
                  value={descripcionIncidencia}
                  onChange={(e) => setDescripcionIncidencia(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!puedeConfirmar || isLoading}>
            {isLoading ? t("common.loading") : t("muestras.cierre.confirmar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
